import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewEncapsulation, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges, numberAttribute, inject, input, output, viewChild } from '@angular/core';
import { fromEvent, Subject, Observable, Subscription } from 'rxjs';
import { takeUntil, sampleTime } from 'rxjs/operators';

import { ScriptLoaderService } from '../../services/script-loader.service';
import { NumberService } from '../../services/number/number.service';
import { IdService } from '../../services/id.service';
import { WINDOW_TOKEN } from '../../services/window.token';
import { DataAnalyticsTaggingEvent } from '../../features/data-analytics-tagging/data-analytics-tagging.domain';
import { DataAnalyticsTaggingConstants } from '../../features/data-analytics-tagging/data-analytics-tagging.constants';
import { DataAnalyticsTaggingDirective } from '../../features/data-analytics-tagging/data-analytics-tagging.directive';
import { BRIGHTCOVE_CONFIGURATION } from '../core-injection.tokens';
import {
  BrightcovePlayer,
  BrightcoveConfiguration,
  BrightcoveDataSettings,
  BrightcoveCue,
  BrightcovePlayerMediaInfo,
  BrightcoveEvent
} from './brightcove-player.domain';
import { BrightcovePlayerService } from './brightcove-player.service';
import { BrightcovePlayerRestService } from './brightcove-player-rest.service';

@Component({
  selector: 'td-wb-brightcove-player',
  templateUrl: 'brightcove-player.component.html',
  styleUrls: ['brightcove-player.component.scss'],
  providers: [
    BrightcovePlayerService,
    BrightcovePlayerRestService
  ],
  imports: [
    DataAnalyticsTaggingDirective,
    DataAnalyticsTaggingDirective
  ],
  encapsulation: ViewEncapsulation.None
})
export class BrightcovePlayerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly browserWindow = inject<Window>(WINDOW_TOKEN);
  private readonly brightcoveConfig = inject<BrightcoveConfiguration>(BRIGHTCOVE_CONFIGURATION, { optional: true });
  private readonly brightcovePlayerService = inject(BrightcovePlayerService);
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly idService = inject(IdService);

  private static readonly MIN_VIDEO_PROGRESS_INTERVAL: number = 5;
  private static readonly ID_PREFIX: string = 'td-wb-brightcove-player';

  readonly videoId = input<string>(undefined);
  readonly autoPlay = input<boolean>(false);
  readonly section = input<string>(undefined);

  /* eslint-disable lines-between-class-members */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get currentTime(): number { return this._currentTime; }
  set currentTime(value: number) {
    this._currentTime = numberAttribute(value, 0);
  }
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get timeupdateEnabled(): boolean { return this._timeupdateEnabled; }
  set timeupdateEnabled(value: boolean) { this._timeupdateEnabled = value; }
  /* eslint-enable lines-between-class-members */

  readonly timeupdateChange = output<number>();
  readonly cueChange = output<BrightcoveCue>();
  readonly pausedChange = output<boolean>();

  readonly videoElement = viewChild<ElementRef>('videoPlayer');
  account: string;
  player: string;
  dataSettingsString: string;
  showOverlay: boolean = false;
  readonly id: string;

  private readonly dataSettings: BrightcoveDataSettings = {
    title: true,
    sharing: false,
    endSlate: false,
    embedCode: false,
    playlist: false
  };

  private playerRef: BrightcovePlayer;
  private mediaInfo: BrightcovePlayerMediaInfo;
  private videoOverlay: number = 0;
  private _currentTime: number = 0;
  private _timeupdateEnabled: boolean = false;
  private readonly videoProgressInterval: number;
  private readonly destroy: Subject<void> = new Subject<void>();
  private timeUpdateInterval: Subscription = Subscription.EMPTY;
  private timeUpdateCurrent: Subscription = Subscription.EMPTY;

  private hasStarted: boolean = false;
  eventData: DataAnalyticsTaggingEvent = {};

  constructor() {
    if ((this.brightcoveConfig ?? null) !== null) {
      this.videoProgressInterval =  numberAttribute(this.brightcoveConfig.videoProgressInterval, 0);
      if (this.videoProgressInterval <= 0) {
        this.videoProgressInterval = BrightcovePlayerComponent.MIN_VIDEO_PROGRESS_INTERVAL;
      }
    }
    this.id = this.idService.generateNamespaceSequentialId(BrightcovePlayerComponent.ID_PREFIX);
  }

  ngOnInit(): void {
    if ((this.brightcoveConfig ?? null) !== null) {
      this.account = this.brightcoveConfig.brightcoveAccount;
      this.player = this.brightcoveConfig.brightcovePlayer;
      this.videoOverlay = numberAttribute(this.brightcoveConfig.brightcoveVideoOverlay, 0);
    }
    this.dataSettingsString = JSON.stringify(this.dataSettings);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.videoId !== undefined
        && changes.videoId.previousValue !== undefined
        && changes.videoId.currentValue !== changes.videoId.previousValue
    ) {
      this.changeVideo();
    }
  }

  ngAfterViewInit(): void {
    if (this.brightcoveConfig?.brightcoveURL) {
      // @ts-expect-error TODO figure out a way to type scriptloader
      this.scriptLoader.load(this.brightcoveConfig.brightcoveURL).then((result: Array< { status: string}>): void => {
        if (result !== undefined && result.length > 0 && result[0].status === 'Already Loaded') {
          const bc = this.browserWindow['bc'];
          this.playerRef = bc(this.videoElement().nativeElement);
        } else {
          const videoJs = this.browserWindow['videojs'];
          this.playerRef = videoJs(this.id);
        }
        if (this.playerRef !== undefined) {
          this.setupEvents();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    /*
    * TODO: look into best way to dispose player, bc script throws console error after dispose.
    * https://support.brightcove.com/brightcove-player-sample-loading-player-dynamically#Removing_a_player
    * You do NOT want to remove the player from the DOM via JavaScript as this could cause memory leaks.
    * When you use the dispose() method, the player will remove itself from the DOM.
    */
    if (this.playerRef !== undefined) {
      this.playerRef.dispose();
    }
  }

  private setupEvents(): void {
    this.brightcovePlayerService.keepSessionAliveOnPlay(this.playerRef);

    fromEvent(this.playerRef, BrightcoveEvent.LOAD_START)
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        this.mediaInfo = this.playerRef.mediainfo;
      });

    fromEvent(this.playerRef, BrightcoveEvent.ENDED)
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:video:${this.section()}:complete`);
      });

    fromEvent(this.playerRef, BrightcoveEvent.LOADED_DATA)
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        this.setupTimeupdate();
        if (this.autoPlay()) {
          this.playerRef.play();
        }
        this.cuePoint();
      });

    fromEvent(this.playerRef, BrightcoveEvent.PLAY)
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        if (!this.hasStarted){
          this.hasStarted = true;
          const section = this.section();
          if (section){
            this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:video:${section}:play`);
          }else {//For EasyTrade
            this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData('tdw:et:learn:video:clicked');
          }
        }
        this.playPause();
      });

    fromEvent(this.playerRef, BrightcoveEvent.PAUSE)
      .pipe(takeUntil(this.destroy))
      .subscribe((): void => {
        this.playPause();
      });
  }

  private setupTimeupdate(): void {
    if (this.timeupdateEnabled) {
      this.setCurrentTime();

      this.timeUpdateInterval.unsubscribe();
      this.timeUpdateCurrent.unsubscribe();

      const timeUpdate: Observable<void>  = fromEvent(this.playerRef, BrightcoveEvent.TIME_UPDATE);
      this.timeUpdateInterval = timeUpdate.pipe(
        sampleTime(this.videoProgressInterval * 1000),
        takeUntil(this.destroy)
      )
        .subscribe((): void => {
          const currentTime = this.playerRef.currentTime();
          const currentTimeinMilliseconds  = NumberService.round(currentTime * 1000);
          this.timeupdateChange.emit(currentTimeinMilliseconds);
        });
      this.timeUpdateCurrent = timeUpdate
        .pipe(takeUntil(this.destroy))
        .subscribe((): void => {
          this.overlay(this.playerRef.currentTime());
        });
    }
  }

  private setCurrentTime(): void {
    if (this.currentTime === undefined) {
      return;
    }
    const currentTimeinSeconds = this.currentTime / 1000;
    this.playerRef.currentTime(currentTimeinSeconds);
  }

  private cuePoint(): void {
    const textTracks = this.playerRef.textTracks();
    if (textTracks !== undefined && textTracks.length > 0) {
      const textTracksFirst = textTracks[0];
      textTracksFirst.oncuechange = (): void => {
        if (textTracksFirst.activeCues[0] !== undefined) {
          this.cueChange.emit(textTracksFirst.activeCues[0]);
        }
      };
    }
  }

  private overlay(currentTime: number): void {
    if (!this.showOverlay && this.videoOverlay > 0 && this.mediaInfo !== undefined) {
      const triggerTime =  this.mediaInfo.duration - this.videoOverlay;
      if (currentTime >= triggerTime) {
        this.showOverlay = true;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  private playPause(): void {
    const paused = this.playerRef.paused();
    this.pausedChange.emit(paused);
  }

  private changeVideo(): void {
    if (this.playerRef !== undefined) {
      this.showOverlay = false;
      this.timeUpdateInterval.unsubscribe();
      this.timeUpdateCurrent.unsubscribe();
      this.playerRef.catalog.getVideo(this.videoId(), (_error: string, video: BrightcovePlayerMediaInfo): void => {
        this.playerRef.catalog.load(video);
        this.playerRef.play();
        this.videoElement().nativeElement.focus();
      });
    }
  }
}
